import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Info, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import WalletMenu from '../components/WalletMenu';
import NavigationMenu from '../components/NavigationMenu';

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  link: string;
  image_url: string;
  date: string;
}

const BlogPosts: React.FC = () => {
  // Static data for blog posts
  const posts: BlogPost[] = [
    {
      id: '1',
      title: 'The Cost of Private Jet Travel',
      excerpt: 'Charter vs. Ownership',
      link: 'https://www.privatecharterx.blog/private-jet-cost-charter-vs-ownership',
      image_url: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/webappimages/niklas-jonasson-OjxpywWo9HI-unsplash-scaled.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5X2MyODA1ZWVkLWRiNGMtNGQ1NC1iNzI4LWJmNDIxMDkzNGVjYiJ9.eyJ1cmwiOiJ3ZWJhcHBpbWFnZXMvbmlrbGFzLWpvbmFzc29uLU9qeHB5d1dvOUhJLXVuc3BsYXNoLXNjYWxlZC5qcGciLCJpYXQiOjE3NDY1MjE5NTcsImV4cCI6MTMxMzQ2NTIxOTU3fQ.4838I_K24H5c4kbtH2ZE9Zr92Byeq8np8NGE6FSf2rI',
      date: '2023-10-10',
    },
    {
      id: '2',
      title: 'The truth about Flying taxis',
      excerpt: 'what no one’s telling you about this 137B Industry',
      link: 'https://www.privatecharterx.blog/truth-about-flying-taxis',
      image_url: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/webappimages/pexels-hyundaimotorgroup-20262953-scaled.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5X2MyODA1ZWVkLWRiNGMtNGQ1NC1iNzI4LWJmNDIxMDkzNGVjYiJ9.eyJ1cmwiOiJ3ZWJhcHBpbWFnZXMvcGV4ZWxzLWh5dW5kYWltb3Rvcmdyb3VwLTIwMjYyOTUzLXNjYWxlZC5qcGciLCJpYXQiOjE3NDY1MjE2OTIsImV4cCI6MTQ3MDY1MjE2OTJ9.5-SIy2MXxvsNFmESvFvf26IXIxNCzzXx5bgDjhkcqOs',
      date: '2023-10-15',
    },
    {
      id: '3',
      title: 'The Rise of AI in Luxury Travel',
      excerpt: 'How Technology is Redefining Luxury and Affordability.',
      link: 'https://www.privatecharterx.blog/future-of-ai-powered-travel-agencies/',
      image_url: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/webappimages/igor-omilaev-eGGFZ5X2LnA-unsplash.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5X2MyODA1ZWVkLWRiNGMtNGQ1NC1iNzI4LWJmNDIxMDkzNGVjYiJ9.eyJ1cmwiOiJ3ZWJhcHBpbWFnZXMvaWdvci1vbWlsYWV2LWVHR0ZaNVgyTG5BLXVuc3BsYXNoLmpwZyIsImlhdCI6MTc0NjUyMTY1NCwiZXhwIjoxNDcwNjUyMTY1NH0.Fsi8KdWHOIOTxAiyKqvRrHfTetMa11FJbD7Al89i_Pk',
      date: '2024-10-18',
    },
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Latest Blog Posts</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with our latest insights and news about private aviation, yachting, finances and more.
            </p>
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border border-gray-100 cursor-pointer group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image_url} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-2">{post.title}</h3>
                  <p className="text-gray-600 mb-3">{post.excerpt}</p>
                  <a 
                    href={post.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs bg-black text-white px-3 py-1.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  >
                    Read More
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="bg-black text-white p-12 rounded-2xl text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">want to learn more about the industries?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Check out our latest blogposts and learn more about private aviation, yachting, luxury cars and web3 based blockchain news and more.
            </p>
            <a 
              href="https://www.privatecharterx.blog" 
    target="_blank" 
    rel="noopener noreferrer" 
    className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
  >
             go to our Blog  <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogPosts;